import {ChangeDetectorRef, Component, inject, Input, ViewChild, Output, EventEmitter, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    AccessType,
    ActivateUserCommand,
    AuthClient,
    ConfirmDwaHopewellCommand,
    ConfirmDwaNestleCommand,
    ConfirmNextViewHopewellCommand,
    ConfirmPasswordResetCommand,
    ConfirmSapNestleCommand,
    EmployeesClient,
    UsersClient,
    getSizeQualifier,
    NestleStatus,
    PermissionType,
    TerminateUserCommand
} from '@services';

import notify from 'devextreme/ui/notify';
import {
    DxButtonModule,
    DxDropDownButtonModule,
    DxSelectBoxModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxNumberBoxModule,
    DxDateBoxModule,
    DxLoadPanelModule,
    DxFileUploaderModule,
    DxValidatorModule,
    DxValidationGroupModule,
    DxSwitchModule,
    DxTagBoxModule, DxValidationGroupComponent
} from 'devextreme-angular';

import {ChangeProfilePasswordFormComponent} from 'src/app/components';
import {FormPopupComponent} from 'src/app/components/utils/form-popup/form-popup.component';
import {AuthService, ScreenService} from 'src/app/services';
import {ActivatedRoute} from "@angular/router";
import {ContactInfoComponent} from './contact-info/contact-info.component';
import {EmergencyDetailsComponent} from './emergency-details/emergency-details.component';
import {SecuritySettingsComponent} from "./security-settings/security-settings.component";
import {ShiftDetailsComponent} from "./shift-details/shift-details.component";
import {SupplierDetailsComponent} from "./supplier-details/supplier-details.component";
import {CarrierDetailsComponent} from "./carrier-details/carrier-details.component";
import {FinancialDetailsComponent} from './financial-details/financial-details.component';
import {EmploymentDetailsComponent} from './employment-details/employment-details.component';
import {NestleDetailsComponent} from './nestle-details/nestle-details.component';
import {NotesComponent, NoteType} from "../../../components/library/notes/notes.component";
import {BehaviorSubject, catchError, finalize, forkJoin, Observable, of, switchMap, tap} from 'rxjs';
import {UserRole} from "../../../shared/constants";
import { VendorDetailsComponent } from './vendor-details/vendor-details.component';
import { CustomerDetailsComponent } from './customer-details/customer-details.component';
import { SiteDetailsComponent } from './site-details/site-details.component';
import { AgencyDetailsComponent } from './agency-details/agency-details.component';
import {UserStatusUpdateMode, UserStatusUpdatePopupComponent} from './user-status-update-popup/user-status-update-popup.component';
import {IssuePasswordPopupComponent} from './issue-password-popup/issue-password-popup.component';

interface MoreMenuAction {
    id: 'sendInvitation' | 'issuePassword' | 'deactivate' | 'activate' | 'terminate' | 'delimitPosition';
    text: string;
    icon: string;
    color?: 'danger' | 'success';
}

interface NestleConfirmAction {
    status: NestleStatus;
    text: string;
    permission: PermissionType;
    confirm: (userId: string) => Observable<void>;
}

@Component({
    selector: 'app-employee-profile',
    templateUrl: './employee-profile.component.html',
    providers: [],
    imports: [
        DxButtonModule,
        DxDropDownButtonModule,
        DxDateBoxModule,
        DxFileUploaderModule,
        DxNumberBoxModule,
        DxToolbarModule,
        DxSelectBoxModule,
        DxLoadPanelModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxValidationGroupModule,
        DxSwitchModule,
        DxTagBoxModule,
        ContactInfoComponent,
        EmergencyDetailsComponent,
        ShiftDetailsComponent,
        SecuritySettingsComponent,
        FinancialDetailsComponent,
        EmploymentDetailsComponent,
        NestleDetailsComponent,
        SupplierDetailsComponent,
        CarrierDetailsComponent,
        VendorDetailsComponent,
        CustomerDetailsComponent,
        SiteDetailsComponent,
        AgencyDetailsComponent,
        ChangeProfilePasswordFormComponent,
        UserStatusUpdatePopupComponent,
        IssuePasswordPopupComponent,
        FormPopupComponent,
        NotesComponent,
        CommonModule
    ]
})
export class EmployeeProfileComponent implements OnInit {
    private ref = inject(ChangeDetectorRef);
    private route = inject(ActivatedRoute);
    protected screen = inject(ScreenService);
    protected authService = inject(AuthService);
    private usersClient = inject(UsersClient);
    private employeesClient = inject(EmployeesClient);
    private authClient = inject(AuthClient);

    @ViewChild('validationGroup', {static: true}) validationGroup: DxValidationGroupComponent;
    @ViewChild(ContactInfoComponent) contactInfoComponent: ContactInfoComponent;
    @ViewChild(EmergencyDetailsComponent) emergencyDetailsComponent: EmergencyDetailsComponent;
    @ViewChild(ShiftDetailsComponent) shiftDetailsComponent: ShiftDetailsComponent;
    @ViewChild(EmploymentDetailsComponent) employmentDetailsComponent: EmploymentDetailsComponent;
    @ViewChild(NestleDetailsComponent) nestleDetailsComponent: NestleDetailsComponent;
    @ViewChild(SupplierDetailsComponent) supplierProfileComponent: SupplierDetailsComponent;
    @ViewChild(CarrierDetailsComponent) carrierProfileComponent: CarrierDetailsComponent;
    @ViewChild(VendorDetailsComponent) vendorProfileComponent: VendorDetailsComponent;
    @ViewChild(CustomerDetailsComponent) customerProfileComponent: CustomerDetailsComponent;
    @ViewChild(SiteDetailsComponent) siteProfileComponent: SiteDetailsComponent;
    @ViewChild(AgencyDetailsComponent) agencyProfileComponent: AgencyDetailsComponent;
    @ViewChild(SecuritySettingsComponent) securitySettingsComponent: SecuritySettingsComponent;

    @Input() isEmployee: boolean = false;
    @Output() profileSaved = new EventEmitter<void>();

    protected isLoading = false;
    protected isFormVisible = true;
    protected isChangePasswordPopupOpened = false;
    protected isStatusUpdatePopupOpened = false;
    protected isPasswordResetPopupOpened = false;
    protected isIssuePasswordPopupOpened = false;
    protected statusUpdateMode: UserStatusUpdateMode = 'confirmTermination';
    protected isDataChanged = false;
    protected isActionRunning = false;
    protected moreMenuActions: MoreMenuAction[] = [];
    protected nestleSystemAccess?: boolean;
    protected nestleStatus?: NestleStatus;
    protected readonly getSizeQualifier = getSizeQualifier;
    protected readonly NoteType = NoteType;

    protected readonly isSupplier$ = new BehaviorSubject<boolean>(false);
    protected readonly isCarrier$ = new BehaviorSubject<boolean>(false);
    protected readonly isVendor$ = new BehaviorSubject<boolean>(false);
    protected readonly isCustomer$ = new BehaviorSubject<boolean>(false);
    protected readonly isFinance$ = new BehaviorSubject<boolean>(false);
    protected readonly isAgency$ = new BehaviorSubject<boolean>(false);

    // A non-administrator may not edit an administrator's profile.
    private isTargetAdministrator = false;
    private isSuspended = false;
    private isTerminated = false;
    private isPositionDelimitable = false;
    private isPasswordIssuable = false;

    private readonly nestleConfirmActions: NestleConfirmAction[] = [
        {
            status: NestleStatus.SapPendingNestle,
            text: 'Confirm SAP',
            permission: PermissionType.ConfirmSapNestle,
            confirm: userId => this.employeesClient.confirmSapNestle(new ConfirmSapNestleCommand({userId}))
        },
        {
            status: NestleStatus.DwaPendingNestle,
            text: 'Confirm DWA',
            permission: PermissionType.ConfirmDwaNestle,
            confirm: userId => this.employeesClient.confirmDwaNestle(new ConfirmDwaNestleCommand({userId}))
        },
        {
            status: NestleStatus.DwaPendingHopewell,
            text: 'Confirm DWA',
            permission: PermissionType.ConfirmDwaHopewell,
            confirm: userId => this.employeesClient.confirmDwaHopewell(new ConfirmDwaHopewellCommand({userId}))
        },
        {
            status: NestleStatus.NextViewPendingHopewell,
            text: 'Confirm NextView',
            permission: PermissionType.ConfirmNextViewHopewell,
            confirm: userId => this.employeesClient.confirmNextViewHopewell(new ConfirmNextViewHopewellCommand({userId}))
        },
    ];

    ngOnInit(): void {
        this.isLoading = true;
        this.usersClient.getUserRoles(this.userId).subscribe({
            next: (roles: string[]) => {
                this.isSupplier$.next(roles.includes(UserRole.Supplier));
                this.isCarrier$.next(roles.includes(UserRole.Carrier));
                this.isVendor$.next(roles.includes(UserRole.Vendor));
                this.isCustomer$.next(roles.includes(UserRole.Customer));
                this.isFinance$.next(roles.includes(UserRole.Finance));
                this.isAgency$.next(roles.includes(UserRole.Agency));
                this.isTargetAdministrator = roles.includes(UserRole.Administrator);
                this.isLoading = false;
                this.loadUserStatus();
                this.loadNestleStatus();
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    protected get isAdminProfileReadOnly(): boolean {
        return this.isTargetAdministrator && !this.authService.isAdministrator;
    }

    private canRunAction(permission: PermissionType): boolean {
        return !this.isAdminProfileReadOnly && this.authService.checkPermission(permission, AccessType.Manage);
    }

    private get canSendInvitation(): boolean {
        return this.canRunAction(PermissionType.SendInvitationEmail);
    }

    private get canDeactivate(): boolean {
        return this.canRunAction(PermissionType.DeactivateUser);
    }

    private get canTerminate(): boolean {
        return !this.authService.isOwnProfile(this.userId) && this.canRunAction(PermissionType.TerminateUser);
    }

    private get canActivate(): boolean {
        return this.canRunAction(PermissionType.ActivateUser);
    }

    private get canIssuePassword(): boolean {
        return this.canRunAction(PermissionType.IssuePassword);
    }

    private get canDelimitPosition(): boolean {
        return this.canConfirmNestle(PermissionType.DelimitPosition);
    }

    private canConfirmNestle(permission: PermissionType): boolean {
        return this.isEmployee && this.canRunAction(permission);
    }

    protected get canConfirmTermination(): boolean {
        return this.nestleStatus === NestleStatus.TerminationPending && this.canConfirmNestle(PermissionType.ConfirmTermination);
    }

    protected get canConfirmDeactivation(): boolean {
        return this.nestleStatus === NestleStatus.DeactivationPending && this.canConfirmNestle(PermissionType.ConfirmDeactivation);
    }

    protected get canConfirmPasswordReset(): boolean {
        return this.nestleStatus === NestleStatus.PasswordResetRequired && this.canConfirmNestle(PermissionType.ConfirmPasswordReset);
    }

    protected get nestleConfirmAction(): NestleConfirmAction | undefined {
        if (this.nestleSystemAccess === false) return undefined;

        const action = this.nestleConfirmActions.find(a => a.status === this.nestleStatus);
        return action && this.canConfirmNestle(action.permission) ? action : undefined;
    }

    get userId() {
        return this.route.snapshot.paramMap.get('employeeId') ?? this.authService.userId;
    }

    changePassword() {
        this.isChangePasswordPopupOpened = true;
    };

    cancel() {
        this.isDataChanged = false;

        // Reload children to revert changes
        this.contactInfoComponent?.loadData();
        this.emergencyDetailsComponent?.loadData();
        this.shiftDetailsComponent?.loadData();
        this.employmentDetailsComponent?.loadData();
        this.nestleSystemAccess = undefined;
        this.nestleDetailsComponent?.loadData();
        this.supplierProfileComponent?.loadData();
        this.carrierProfileComponent?.loadData();
        this.vendorProfileComponent?.loadData();
        this.customerProfileComponent?.loadData();
        this.siteProfileComponent?.loadData();
        this.agencyProfileComponent?.loadData();
        this.securitySettingsComponent?.loadData();
        this.ref.detectChanges();
    }

    save() {
        if (!this.validate()) return;

        this.saveChanges().subscribe({
            next: () => {
                this.loadNestleStatus();
                this.loadUserStatus();
                // Picks up an expiry date set by the save, so the next save doesn't send the stale blank back.
                this.nestleDetailsComponent?.loadData();
            },
            error: () => {
                // Errors handled individually in pipes or child component
            }
        });
    }

    protected confirmNestleStep() {
        const action = this.nestleConfirmAction;
        if (!action || !this.validate(true)) return;

        this.isActionRunning = true;

        (this.isDataChanged ? this.saveChanges() : of(null)).pipe(
            switchMap(() => action.confirm(this.userId).pipe(
                tap(() => notify({message: 'Nestle Status updated', position: 'bottom center'}, 'success', 3000)),
                catchError((errors: Array<any>) => {
                    this.notifyErrors(errors);
                    return of(null);
                })
            )),
            switchMap(() => this.employeesClient.getNestleStatus(this.userId)),
            finalize(() => this.isActionRunning = false)
        ).subscribe({
            next: dto => this.nestleStatus = dto.status,
            error: () => {
                // Save errors are handled individually in child components
            }
        });
    }

    private validate(requireNestleDetails = false): boolean {
        if (this.nestleDetailsComponent)
            this.nestleDetailsComponent.requireAllFields = requireNestleDetails;

        const result = this.validationGroup.instance.validate();

        if (this.nestleDetailsComponent)
            this.nestleDetailsComponent.requireAllFields = false;

        if (!result.isValid)
            notify({message: 'Please fix validation errors', position: 'bottom center'}, 'error', 3000);

        return !!result.isValid;
    }

    private saveChanges(): Observable<unknown> {
        // Save Contact Info
        const saveContact$ = this.contactInfoComponent
            ? this.contactInfoComponent.save()
            : of(null);

        // Save Emergency Details
        const saveEmergency$ = this.emergencyDetailsComponent
            ? this.emergencyDetailsComponent.save()
            : of(null);

        // Save Shift Details
        const saveShift$ = this.shiftDetailsComponent
            ? this.shiftDetailsComponent.save()
            : of(null);

        // Save Employment Details
        const saveEmployment$ = this.employmentDetailsComponent
            ? this.employmentDetailsComponent.save()
            : of(null);

        // Save Nestle Details
        const saveNestle$ = this.nestleDetailsComponent
            ? this.nestleDetailsComponent.save()
            : of(null);

        // Employment Details go last: a first Nestle Status sets the password expiry date, which the Nestle form's blank value would overwrite.
        const saveNestleThenEmployment$ = saveNestle$.pipe(switchMap(() => saveEmployment$));

        // Save Supplier Details
        const saveSupplier$ = this.supplierProfileComponent
            ? this.supplierProfileComponent.save()
            : of(null);

        // Save Carrier Details
        const saveCarrier$ = this.carrierProfileComponent
            ? this.carrierProfileComponent.save()
            : of(null);

        // Save Vendor Details
        const saveVendor$ = this.vendorProfileComponent
            ? this.vendorProfileComponent.save()
            : of(null);

        // Save Customer Details
        const saveCustomer$ = this.customerProfileComponent
            ? this.customerProfileComponent.save()
            : of(null);

        // Save Site Details
        const saveSite$ = this.siteProfileComponent
            ? this.siteProfileComponent.save()
            : of(null);

        const saveAgency$ = this.agencyProfileComponent
            ? this.agencyProfileComponent.save()
            : of(null);

        // Save Security Settings
        const saveSecurity$ = this.securitySettingsComponent
            ? this.securitySettingsComponent.save()
            : of(null);

        // Execute all
        return forkJoin([saveContact$, saveEmergency$, saveShift$, saveNestleThenEmployment$, saveSupplier$, saveCarrier$, saveVendor$, saveCustomer$, saveSite$, saveAgency$, saveSecurity$]).pipe(
            tap(() => {
                this.isDataChanged = false;
                notify({message: 'Data saved', position: 'bottom center'}, 'success');
                this.profileSaved.emit();
            })
        );
    }

    protected onFieldChange() {
        this.isDataChanged = true;
    }

    protected onMoreActionClick(e: { itemData?: MoreMenuAction }) {
        switch (e.itemData?.id) {
            case 'sendInvitation':
                this.sendInvitation();
                break;
            case 'issuePassword':
                this.isIssuePasswordPopupOpened = true;
                break;
            case 'deactivate':
                this.runStatusAction(this.employeesClient.suspend(this.userId), 'User has been deactivated');
                break;
            case 'activate':
                this.runStatusAction(this.employeesClient.activateUser(new ActivateUserCommand({userId: this.userId})), 'User has been activated');
                break;
            case 'terminate':
                this.runStatusAction(this.employeesClient.terminateUser(new TerminateUserCommand({userId: this.userId})), 'User has been terminated');
                break;
            case 'delimitPosition':
                this.openStatusUpdate('delimitPosition');
                break;
        }
    }

    protected confirmPasswordReset() {
        this.isPasswordResetPopupOpened = false;
        this.runStatusAction(this.employeesClient.confirmPasswordReset(new ConfirmPasswordResetCommand({userId: this.userId})), 'Password reset confirmed');
    }

    protected openStatusUpdate(mode: UserStatusUpdateMode) {
        this.statusUpdateMode = mode;
        this.isStatusUpdatePopupOpened = true;
    }

    protected refreshStatus() {
        this.nestleSystemAccess = undefined;
        this.loadUserStatus();
        this.loadNestleStatus();
        this.employmentDetailsComponent?.loadData();
        this.nestleDetailsComponent?.loadData();
    }

    private loadUserStatus() {
        if (!this.canSendInvitation && !this.canIssuePassword && !this.canDeactivate && !this.canTerminate && !this.canActivate && !this.canDelimitPosition) return;

        this.employeesClient.getUserStatus(this.userId).subscribe(status => {
            this.isSuspended = status.suspended;
            this.isTerminated = status.terminated;
            this.isPositionDelimitable = status.canDelimitPosition;
            this.isPasswordIssuable = status.canIssuePassword;
            // Patched rather than reloaded: reloading Security & Permissions marks the profile as changed.
            if (this.securitySettingsComponent?.securityData) {
                this.securitySettingsComponent.securityData.status = status.status;
            }
            this.buildMoreMenu();
        });
    }

    private loadNestleStatus() {
        const canViewNestleDetails = this.authService.canViewProfileSection(this.userId, PermissionType.EmployeeNestleDetailsOwn, PermissionType.EmployeeNestleDetailsOthers);
        const canConfirm = this.nestleConfirmActions.some(a => this.canConfirmNestle(a.permission))
            || this.canConfirmNestle(PermissionType.ConfirmTermination)
            || this.canConfirmNestle(PermissionType.ConfirmDeactivation)
            || this.canConfirmNestle(PermissionType.ConfirmPasswordReset);
        if (!this.isEmployee || (!canViewNestleDetails && !canConfirm)) return;

        this.employeesClient.getNestleStatus(this.userId).subscribe(dto => this.nestleStatus = dto.status);
    }

    private buildMoreMenu() {
        const actions: MoreMenuAction[] = [];
        const activate: MoreMenuAction = {id: 'activate', text: 'Activate User', icon: 'fa-light fa-square-check', color: 'success'};

        if (this.canDelimitPosition && this.isPositionDelimitable)
            actions.push({id: 'delimitPosition', text: 'Delimit Position', icon: 'fa-light fa-xmark', color: 'danger'});

        if (this.isTerminated) {
            if (this.canActivate)
                actions.push(activate);
        } else {
            if (this.canSendInvitation && !this.isSuspended)
                actions.push({id: 'sendInvitation', text: 'Send Invitation Email', icon: 'fa-light fa-envelope'});

            if (this.canIssuePassword && this.isPasswordIssuable)
                actions.push({id: 'issuePassword', text: 'Issue Password', icon: 'fa-light fa-key'});

            if (this.isSuspended && this.canActivate)
                actions.push(activate);
            else if (!this.isSuspended && this.canDeactivate)
                actions.push({id: 'deactivate', text: 'Deactivate User', icon: 'fa-light fa-xmark', color: 'danger'});

            if (this.canTerminate)
                actions.push({id: 'terminate', text: 'Terminate User', icon: 'fa-light fa-xmark', color: 'danger'});
        }

        this.moreMenuActions = actions;
    }

    private sendInvitation() {
        this.isActionRunning = true;

        this.authClient.sendInvitation(this.userId).subscribe({
            next: () => {
                this.isActionRunning = false;
                notify({message: 'Invitation email has been sent', position: 'bottom center'}, 'success', 3000);
            },
            error: (errors: Array<any>) => this.onActionError(errors)
        });
    }

    private runStatusAction(action: Observable<void>, message: string) {
        this.isActionRunning = true;

        action.subscribe({
            next: () => {
                this.isActionRunning = false;
                notify({message, position: 'bottom center'}, 'success', 3000);
                this.refreshStatus();
            },
            error: (errors: unknown) => this.onActionError(errors)
        });
    }

    private onActionError(errors: unknown) {
        this.isActionRunning = false;
        this.notifyErrors(errors);
    }

    private notifyErrors(errors: unknown) {
        const messages = Array.isArray(errors) ? errors.map(error => error.message) : ['The action could not be completed'];
        messages.forEach(message => notify({
            message,
            type: 'error',
            displayTime: 5000,
        }, {
            position: 'bottom right',
            direction: 'up-push'
        }));
    }

    protected rolesChanged(roles: string[]) {
        this.isDataChanged = true;
        this.isSupplier$.next(roles.includes('Supplier'));
        this.isCarrier$.next(roles.includes('Carrier'));
        this.isVendor$.next(roles.includes('Vendor'));
        this.isCustomer$.next(roles.includes('Customer'));
        this.isFinance$.next(roles.includes('Finance'));
        this.isAgency$.next(roles.includes(UserRole.Agency));
    }

    protected readonly PermissionType = PermissionType;
}
